<template>
  <el-dialog v-model="showDialog" :title="$t(`components.create-template.header`)" @close="cancelCreate">
    <el-form ref="dataForm" :model="loadingData" label-position="top" status-icon>
      <!-- ProductType Form -->
      <el-form-item prop="productType" :label="$t('components.create-template.producttype')">
        <el-select v-model="loadingData.productType" :disabled="isFetchingProductTypeList">
          <!--<el-option v-for="productType in productTypeList" :key="productType" :value="productType" :label="$t(`enum.product-type.${productType}`)" />-->
          <el-option v-for="productType in productTypeList" :key="productType" :value="productType" :label="productType" />
        </el-select>
      </el-form-item>

      <!-- Mesh Name -->
      <el-form-item
        prop="title"
        :label="$t('components.create-template.title')"
        :rules="{
          required: true,
          message: $t('error.vuelidate.required'),
          trigger: 'blur',
        }"
      >
        <el-input v-model="loadingData.title" />
      </el-form-item>

      <!-- Mesh Description -->
      <el-form-item
        prop="description"
        :label="$t('components.create-template.description')"
        :rules="{
          required: true,
          message: $t('error.vuelidate.required'),
          trigger: 'blur',
        }"
      >
        <el-input v-model="loadingData.description" :rows="2" type="textarea" />
      </el-form-item>

      <!-- Upload Product Image  -->
      <el-form-item
        prop="thumbnail.content"
        :label="$t('components.create-template.thumbnail')"
        :rules="{
          required: true,
          message: $t('error.vuelidate.required'),
          trigger: 'blur',
        }"
      >
        <el-upload
          v-model:file-list="productImageFiles"
          multiple
          action="#"
          drag
          list-type="picture-card"
          :before-upload="beforeImageUpload"
          :http-request="(res: any) => uploadFile(res, ImageType.product)"
          accept=".jpg,.png,"
        >
          <template #default>
            <div>
              <font-awesome-icon icon="upload" class="primary big" />
              <br />
              {{ $t('components.create-template.upload-image') }}
            </div>
          </template>
          <template #file="{ file }">
            <PreviewRendererImg
              :meshes="[
                {
                  thumbnailFiletype: getImageFileType(file.name),
                  thumbnail: file.name,
                  thumbnailUrl: file.url,
                },
              ]"
              :can-select="false"
              :can-remove="true"
              @remove-item="handleRemove(file, ImageType.product)"
            />
          </template>
        </el-upload>
      </el-form-item>

      <!-- Upload Product Video  -->
      <el-form-item
        prop="video.content"
        :label="$t('components.create-template.video')"
      >
        <el-upload
          v-model:file-list="productVideoFiles"
          multiple
          action="#"
          drag
          list-type="picture-card"
          :before-upload="beforeVideoUpload"
          :http-request="(res: any) => uploadFile(res, ImageType.video)"
          accept=".mp4,.webm,.mov,.mkv,.avi"
        >
          <template #default>
            <div>
              <font-awesome-icon icon="upload" class="primary big" />
              <br />
              {{ $t('components.create-template.upload-video') }}
            </div>
          </template>
          <template #file="{ file }">
            <PreviewRendererVideo
              :meshes="[
                {
                  videoFiletype: getVideoFileType(file.name),
                  video: file.name,
                  videoUrl: file.url,
                  videoThumbnailFiletype: loadingData.video.thumbnailType,
                  videoThumbnail: loadingData.video.thumbnailName,
                  videoThumbnailUrl: loadingData.video.thumbnailContent,
                },
              ]"
              :can-remove="false"
              :can-play="true"
              :is-loading="uploadVideoLoading"
              @remove-item="handleRemove(file, ImageType.video)"
            />
          </template>
        </el-upload>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button native-type="submit" type="primary" class="btn btn-primary" @click="save()">
        {{ $t('components.create-template.save') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { Vue, Prop, Component } from 'vue-facing-decorator';
import { UploadFile } from 'element-plus/es/components/upload/src/upload';
import { ElForm, ElMessage, ElMessageBox } from 'element-plus';
import { cloneDeep } from '@apollo/client/utilities';

import { ProductType, getFileType, getImageFileType, getVideoFileType, isValidImageFileType, isValidVideoFileType } from '@/types/enum/upload';
import PreviewRendererVideo from '@/components/features/previewRenderer/PreviewRendererVideo.vue';
import PreviewRendererImg from '@/components/features/previewRenderer/PreviewRendererImg.vue';
import { GetAllDistinctProductTypes } from '@/services/api/productService';
import { FileData } from '@/types/enum/file';
import { deleteFileFromAzure, fileContentToBase64, generateVideoThumbnail, ProductVideoFiles, uploadToAzure } from '@/utils/file';
import * as ModelService from '@/services/api/modelService';
import { File as UtilityFile } from '@/types/api/Utility/File';
import { Video } from '@/types/api/Utility/Video';
import { useRouter } from 'vue-router';

interface LoadingData {
  productType: ProductType;
  title: string;
  description: string;
  thumbnail: FileWithUid;
  video: Video;
}

export type FileWithUid = UtilityFile & { uid?: number };
export enum ImageType {
  'product' = 'product',
  'video' = 'video',
}

@Component({
  components: { PreviewRendererImg, PreviewRendererVideo },
  emits: [],
})
/* eslint-disable @typescript-eslint/no-explicit-any*/
@Component
export default class CreateTemplate extends Vue {
  @Prop({ default: 'other' }) directoryPath!: string;

  router = useRouter();
  selected = -1;
  showDialog = true;
  loadingData: LoadingData = {
    productType: ProductType.fence, // Add this line
    title: '',
    description: '',
    thumbnail: new UtilityFile(),
    video: new Video(),
  };

  // note: state for product image thumbnails refers to selected input format. Separate it from selectedInput state since it needs to be multiple  (it's for payload ModelService.uploadNewProduct).
  selectedProductImages: FileWithUid[] = [];
  // note: state for controlled el upload product image, keeping ui when clicking prev step
  productImageFiles: FileData[] = [];
  productVideoFiles: ProductVideoFiles[] = [];

  isFetchingProductTypeList = false;
  productTypeList = [];
  uploadVideoLoading = false;

  ImageType = ImageType;

  getFileType = getFileType;
  getImageFileType = getImageFileType;
  getVideoFileType = getVideoFileType;

  mounted(): void {
    this.isFetchingProductTypeList = true;
    GetAllDistinctProductTypes()
      .then((res) => {
        if (res.allDistinctProductTypes) {
          this.productTypeList = res.allDistinctProductTypes.map((p: any) => p.name);
        }
        this.isFetchingProductTypeList = false;
      })
      .catch((err) => {
        this.isFetchingProductTypeList = false;
        console.error('[ERROR GET PRODUCT TYPES]: ', err);
      });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async uploadFile(res: any, imageType: ImageType): Promise<boolean> {
    if (imageType === ImageType.product) {
      fileContentToBase64(res.file, (encodedString) => {
        this.loadingData.thumbnail.name = res.file.name;
        this.loadingData.thumbnail.type = getImageFileType(res.file.name);
        this.loadingData.thumbnail.content = encodedString;
        const newFileData: FileWithUid = new UtilityFile();
        newFileData.name = res.file.name;
        newFileData.type = getImageFileType(res.file.name);
        newFileData.content = encodedString;
        newFileData.uid = res.file.uid;
        this.selectedProductImages = [...cloneDeep(this.selectedProductImages), newFileData];
      });
    }
    if (imageType === ImageType.video) {
      this.uploadVideoFileToAzure(res);
    }
    return true;
  }

  async uploadVideoFileToAzure(res: any): Promise<boolean> {
    this.uploadVideoLoading = true;

    // Get video file
    const videoFile = res.file;
    const videoDirectoryPath = `${this.directoryPath}/media/videos`;

    // Generate video thumbnail file
    const videoThumbnailFile = await generateVideoThumbnail(videoFile);
    const videoThumbnailDirectoryPath = `${this.directoryPath}/media/images`;

    // validation: If there is no videoFile or videoThumbnailFile,
    // then do not proceed to the next step
    if (!videoFile || !videoThumbnailFile) return false;

    try {
      // Upload video
      const videoUrl = await uploadToAzure({
        file: videoFile,
        direcatoryPath: videoDirectoryPath,
      });
      console.log('videoUrl', videoUrl);
      // Upload thumbnail
      const videoThumbnailUrl = await uploadToAzure({
        file: videoThumbnailFile,
        direcatoryPath: videoThumbnailDirectoryPath,
      });
      console.log('videoThumbnailUrl', videoThumbnailUrl);

      // update state video & thumbnail
      await this.setVideoFile(videoFile, videoUrl);
      await this.setVideoThumbnailFile(videoThumbnailFile, videoThumbnailUrl);
      await this.setProductVideoFiles(videoFile);

      alert('Upload successful!');
      this.uploadVideoLoading = false;
      return true;
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Upload failed. Please try again.');
      this.uploadVideoLoading = false;
      return false;
    }
  }

  async setProductVideoFiles(videoFile: FileData): Promise<boolean> {
    if (!videoFile) return false;

    console.log('videoFile', videoFile?.uid);
    return true;
  }

  async setVideoFile(file: File, videoUrl: string): Promise<boolean> {
    if (!file || !videoUrl.length) return false;

    this.loadingData.video.name = file.name;
    this.loadingData.video.type = getVideoFileType(file.name);
    this.loadingData.video.content = videoUrl;
    return true;
  }

  async setVideoThumbnailFile(file: File, videoThumbnailUrl: string): Promise<boolean> {
    if (!file || !videoThumbnailUrl.length) return false;

    this.loadingData.video.thumbnailName = file.name;
    this.loadingData.video.thumbnailType = getImageFileType(file.name);
    this.loadingData.video.thumbnailContent = videoThumbnailUrl;
    return true;
  }

  handleRemove(file: UploadFile, imageType: ImageType): void {
    ElMessageBox.confirm((this as any).$t('components.create-template.deleteMessage'), 'info', {
      confirmButtonText: (this as any).$t('components.create-template.yes'),
      cancelButtonText: (this as any).$t('components.create-template.no'),
      type: 'warning',
    })
      .then(async (confirm) => {
        if (confirm) {
          if (imageType === ImageType.product) {
            this.loadingData.thumbnail.name = '';
            this.loadingData.thumbnail.content = '';
            this.loadingData.thumbnail.type = '';
            this.productImageFiles = cloneDeep(this.productImageFiles).filter((f) => f.uid !== file?.uid);
            this.selectedProductImages = cloneDeep(this.selectedProductImages).filter((f) => f.uid !== file.uid);
          }
          if (imageType === ImageType.video) {
            const uploadNewProductVideoFiles = this.productVideoFiles.filter(({ uid }) => uid !== file.uid);

            if (this.loadingData.video?.content) await deleteFileFromAzure(this.loadingData.video?.content);
            if (this.loadingData.video?.thumbnailContent) await deleteFileFromAzure(this.loadingData.video?.thumbnailContent);

            this.productVideoFiles = uploadNewProductVideoFiles;
            this.loadingData.video.name = null;
            this.loadingData.video.content = null;
            this.loadingData.video.type = null;
            this.loadingData.video.thumbnailName = null;
            this.loadingData.video.thumbnailContent = null;
            this.loadingData.video.thumbnailType = null;
          }
        }
      })
      .catch(() => {
        //
      });
  }

  beforeImageUpload(file: any): boolean {
    if (isValidImageFileType(file.name)) {
      return true;
    }
    ElMessage.error((this as any).$t('components.create-template.wrongType'));
    return false;
  }

  beforeVideoUpload(file: any): boolean {
    if (isValidVideoFileType(file.name)) {
      if (this.loadingData.video.content === null) {
        return true;
      }
      ElMessage.error((this as any).$t('components.create-template.onlyOneVideo'));
      return false;
    }
    ElMessage.error((this as any).$t('components.create-template.wrongType'));
    return false;
  }

  cancelCreate(): void {
    this.router.back();
  }

  save(): void {
    const dataForm = this.$refs.dataForm as typeof ElForm;
    dataForm?.validate(async (valid: any) => {
      if (valid) {
        ModelService.createProduct({
          productName: this.loadingData.title,
          sampleImagePaths: this.selectedProductImages.map((item) => item.getServiceOutput()),
          thumbnail: this.loadingData.thumbnail.getServiceOutput(),
          videos: this.loadingData.video.content ? [this.loadingData.video.getServiceOutput()] : [],
          description: this.loadingData.description,
          productType: this.loadingData.productType,
          basePrice: 100,
        }).then(async (result) => {
          const templateId = result.modelId;
          this.router.replace(`/template-workflow?id=${templateId}`);
        });
      }
    });
  }
}
</script>

<style lang="scss" scoped></style>
